// src/components/AppointmentBookingDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  useTheme,
  alpha,
  Alert,
  CircularProgress
} from '@mui/material';
import { Appointment } from '../types/marketTypes';

interface AppointmentBookingDialogProps {
  open: boolean;
  onClose: () => void;
  orgUniqueId: string;
  serviceName: string;
  providerUserName: string;
}

const AppointmentBookingDialog: React.FC<AppointmentBookingDialogProps> = ({
  open,
  onClose,
  orgUniqueId,
  serviceName,
  providerUserName
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    patientName: '',
    patientUserName: 'guest',
    appointmentNote: '',
    appointmentDate: '',
    appointmentTime: '',
    appointmentVariation: 'standard'
  });

  const generateAppointmentId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `APT-${timestamp}-${random}`;
  };

  const handleSubmit = async () => {
    if (!formData.patientName.trim() || !formData.appointmentDate || !formData.appointmentTime) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appointment: Appointment = {
        appointmentId: generateAppointmentId(),
        orgUniqueId,
        appointmentTitle: `Appointment for ${serviceName}`,
        patientName: formData.patientName,
        patientUserName: formData.patientUserName,
        providerUserName,
        appointmentNote: formData.appointmentNote,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        appointmentStatus: 'scheduled',
        appointmentVariation: formData.appointmentVariation
      };

      const response = await fetch('https://internaltoolsapi.onrender.com/api/Services/BookAppointment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointment)
      });

      if (!response.ok) {
        throw new Error(`Failed to book appointment: ${response.statusText}`);
      }

      setSuccess(true);
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setFormData({
          patientName: '',
          patientUserName: 'guest',
          appointmentNote: '',
          appointmentDate: '',
          appointmentTime: '',
          appointmentVariation: 'standard'
        });
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book appointment');
      console.error('Error booking appointment:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Book Appointment: {serviceName}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Appointment booked successfully!
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Your Name"
              value={formData.patientName}
              onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
              required
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.appointmentDate}
                onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={formData.appointmentTime}
                onChange={(e) => setFormData({ ...formData, appointmentTime: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Additional Notes (Optional)"
              value={formData.appointmentNote}
              onChange={(e) => setFormData({ ...formData, appointmentNote: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            bgcolor: theme.palette.secondary.main,
            '&:hover': { bgcolor: theme.palette.secondary.dark }
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Booking...
            </>
          ) : 'Book Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentBookingDialog;